import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { API_STRING as api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ConfirmDialog from '@/components/ConfirmDialog';
import { Switch } from '@/components/ui/switch';
import { Globe, Info, Rocket, Save, Trash2, Users, Wrench } from 'lucide-react';
import { logger } from '@/utils/logger';

const PAGES = [
  { key: 'browse', label: 'Browse / Landing', desc: 'Home and gift browsing (/, /browse)' },
  { key: 'keepsakes', label: 'Keepsakes', desc: 'Keepsakes, questionnaires, wishlists' },
  { key: 'about', label: 'About', desc: 'The About page' },
  { key: 'profile', label: 'Account / Profile', desc: 'User account settings' },
];

const FEATURES = [
  { key: 'archetype_worlds', label: 'Archetype Worlds', desc: 'The gift-world carousel and world sections on Browse (Curated, Experiences, Handmade, DIY…)' },
  { key: 'brand_directory', label: 'Brand Directory', desc: 'Support-a-Brand: /brands directory, brand pages, the application form, and its Browse world' },
];

export default function AdminSettings() {
  const [state, setState] = useState({ site_enabled: false, pages: {}, message: '' });
  const [features, setFeatures] = useState(null);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [launch, setLaunch] = useState(null);
  const [launchSubject, setLaunchSubject] = useState('');
  const [launchBody, setLaunchBody] = useState('');
  const [sendEmailOnLaunch, setSendEmailOnLaunch] = useState(true);
  const [confirmLaunch, setConfirmLaunch] = useState(false);
  const [confirmPrelaunch, setConfirmPrelaunch] = useState(false);
  const [launchBusy, setLaunchBusy] = useState(false);

  const fetchLaunch = async () => {
    try {
      const res = await fetch(`${api}/admin/launch`, { credentials: 'include' });
      const data = await res.json();
      setLaunch(data);
      setLaunchSubject(data.email_subject || '');
      setLaunchBody(data.email_body || '');
    } catch (err) {
      logger.error('Failed to load launch state:', err);
    }
  };

  useEffect(() => {
    fetchLaunch();
  }, []);

  const saveLaunchTemplate = async () => {
    try {
      const res = await fetch(`${api}/admin/launch/email-template`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ subject: launchSubject, body: launchBody }),
      });
      if (!res.ok) throw new Error('failed');
      toast.success('Launch email draft saved');
    } catch {
      toast.error('Failed to save draft');
    }
  };

  const enterPrelaunch = async () => {
    setLaunchBusy(true);
    try {
      const res = await fetch(`${api}/admin/launch/prelaunch`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error('failed');
      toast.success('🔒 Pre-launch mode ON. Products are hidden behind the coming-soon page.');
      setConfirmPrelaunch(false);
      fetchLaunch();
    } catch {
      toast.error('Failed to enable pre-launch');
    } finally {
      setLaunchBusy(false);
    }
  };

  const doLaunch = async () => {
    setLaunchBusy(true);
    try {
      await saveLaunchTemplate();
      const res = await fetch(`${api}/admin/launch/go`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ send_email: sendEmailOnLaunch }),
      });
      if (!res.ok) throw new Error('failed');
      toast.success(sendEmailOnLaunch
        ? '🚀 Mementa is LIVE! Launch emails are sending in the background.'
        : '🚀 Mementa is LIVE!', { duration: 8000 });
      setConfirmLaunch(false);
      fetchLaunch();
    } catch {
      toast.error('Launch failed');
    } finally {
      setLaunchBusy(false);
    }
  };

  const clearExpiredShortlists = async () => {
    if (!window.confirm('Delete all expired shared shortlist links?')) return;
    setCleaning(true);
    try {
      const res = await fetch(`${api}/admin/shared-shortlists/expired`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error('failed');
      toast.success(`Deleted ${data.deleted ?? 0} expired link(s)`);
    } catch (err) {
      logger.error('Cleanup failed:', err);
      toast.error('Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${api}/maintenance`);
        setState(await res.json());
      } catch (err) {
        logger.error('Failed to load maintenance settings:', err);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
      try {
        const res = await fetch(`${api}/features`);
        setFeatures(await res.json());
      } catch (err) {
        logger.error('Failed to load feature flags:', err);
      }
    })();
  }, []);

  const saveFeatures = async (next) => {
    setSavingFeatures(true);
    try {
      const res = await fetch(`${api}/admin/features`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error('save failed');
      setFeatures(await res.json());
      toast.success('Feature toggles updated');
    } catch (err) {
      logger.error('Failed to save feature flags:', err);
      toast.error('Failed to save feature toggles');
    } finally {
      setSavingFeatures(false);
    }
  };

  const save = async (next) => {
    setSaving(true);
    try {
      const res = await fetch(`${api}/admin/maintenance`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error('save failed');
      setState(await res.json());
      toast.success('Maintenance settings updated');
    } catch (err) {
      logger.error('Failed to save maintenance settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-sm" style={{ color: '#73766A' }}>Loading settings…</div>;
  }

  return (
    <div className="p-4 sm:p-8 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
          <Wrench className="w-6 h-6" style={{ color: '#9E6752' }} /> Site Settings
        </h1>
        <p className="text-sm mt-1" style={{ color: '#73766A' }}>
          Close the whole site or individual pages for maintenance. Admins always bypass maintenance mode.
        </p>
      </div>

      {/* Whole site */}
      <div className="rounded-xl p-5 mb-4 flex items-start justify-between gap-4" style={{ background: state.site_enabled ? '#FEF2F2' : 'white', border: `1px solid ${state.site_enabled ? '#FECACA' : '#E5E7EB'}` }}>
        <div>
          <p className="font-semibold flex items-center gap-2" style={{ color: '#20212B' }}>
            <Globe className="w-4 h-4" style={{ color: state.site_enabled ? '#DC2626' : '#9E6752' }} />
            Whole-site maintenance
          </p>
          <p className="text-sm mt-1" style={{ color: '#73766A' }}>
            Visitors see a branded "We'll be right back" page everywhere. API access is paused for non-admins.
          </p>
        </div>
        <Switch
          checked={state.site_enabled}
          onCheckedChange={(v) => save({ ...state, site_enabled: v })}
          disabled={saving}
          data-testid="maintenance-site-toggle"
        />
      </div>

      {/* Per-page */}
      <div className="rounded-xl mb-4" style={{ background: 'white', border: '1px solid #E5E7EB' }}>
        <p className="px-5 pt-4 pb-1 text-xs font-semibold tracking-wider" style={{ color: '#9E6752' }}>PER-PAGE MAINTENANCE</p>
        {PAGES.map((p, i) => (
          <div key={p.key} className="px-5 py-4 flex items-center justify-between gap-4" style={{ borderTop: i > 0 ? '1px solid #F3F4F6' : 'none' }}>
            <div>
              <p className="font-medium text-sm" style={{ color: '#20212B' }}>{p.label}</p>
              <p className="text-xs mt-0.5" style={{ color: '#73766A' }}>{p.desc}</p>
            </div>
            <Switch
              checked={!!state.pages?.[p.key]}
              onCheckedChange={(v) => save({ ...state, pages: { ...state.pages, [p.key]: v } })}
              disabled={saving}
              data-testid={`maintenance-page-toggle-${p.key}`}
            />
          </div>
        ))}
      </div>

      {/* Feature toggles — new features live/hidden on the public site */}
      <div className="rounded-xl mb-4" style={{ background: 'white', border: '1px solid #E5E7EB' }} data-testid="feature-toggles-card">
        <p className="px-5 pt-4 pb-1 text-xs font-semibold tracking-wider" style={{ color: '#9E6752' }}>FEATURE TOGGLES</p>
        <p className="px-5 text-xs" style={{ color: '#73766A' }}>
          Switch newer features on or off for visitors. OFF = hidden from the public (a friendly "launching soon" note where relevant). Admins always see everything.
        </p>
        {features === null ? (
          <p className="px-5 py-4 text-xs" style={{ color: '#73766A' }}>Loading feature flags…</p>
        ) : (
          FEATURES.map((f, i) => (
            <div key={f.key} className="px-5 py-4 flex items-center justify-between gap-4" style={{ borderTop: i > 0 ? '1px solid #F3F4F6' : 'none' }}>
              <div>
                <p className="font-medium text-sm flex items-center gap-2" style={{ color: '#20212B' }}>
                  {f.label}
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={features[f.key]
                      ? { background: '#DCFCE7', color: '#166534' }
                      : { background: '#FEF3C7', color: '#92400E' }}
                  >
                    {features[f.key] ? 'LIVE' : 'HIDDEN'}
                  </span>
                </p>
                <p className="text-xs mt-0.5" style={{ color: '#73766A' }}>{f.desc}</p>
              </div>
              <Switch
                checked={!!features[f.key]}
                onCheckedChange={(v) => saveFeatures({ ...features, [f.key]: v })}
                disabled={savingFeatures}
                data-testid={`feature-toggle-${f.key}`}
              />
            </div>
          ))
        )}
      </div>

      {/* Message */}
      <div className="rounded-xl p-5" style={{ background: 'white', border: '1px solid #E5E7EB' }}>
        <p className="font-semibold text-sm mb-2" style={{ color: '#20212B' }}>Custom message (optional)</p>
        <textarea
          value={state.message}
          onChange={(e) => setState({ ...state, message: e.target.value })}
          placeholder="e.g. We're adding new gifts, back at 3pm ET!"
          rows={3}
          maxLength={500}
          className="w-full p-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#9E6752]"
          style={{ borderColor: '#D1D5DB', color: '#20212B', background: 'white' }}
          data-testid="maintenance-message-input"
        />
        <div className="flex justify-end mt-3">
          <Button onClick={() => save(state)} disabled={saving} style={{ background: '#9E6752', color: 'white' }} data-testid="maintenance-save-message">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save Message'}
          </Button>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs" style={{ color: '#73766A' }}>
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#9E6752' }} />
        <p>Changes take effect within a few seconds. You'll continue to see the site normally while logged in as an admin, open an incognito window to preview what visitors see.</p>
      </div>

      {/* Launch Control */}
      <div className="bg-white rounded-xl p-5 mt-6 shadow-sm" data-testid="launch-control-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#20212B' }}>
            <Rocket className="w-5 h-5" style={{ color: '#9E6752' }} /> Launch Control
          </h2>
          {launch && (
            <div className="flex items-center gap-2 flex-wrap">
              <span
                data-testid="launch-waitlist-count-badge"
                className="px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                style={{ background: '#F3E8E2', color: '#9E6752' }}
                title="Real accounts on the waitlist (excludes admins and test accounts)"
              >
                <Users className="w-3.5 h-3.5" />
                {launch.waitlist_count ?? 0} on the waitlist
              </span>
              <span
                data-testid="launch-status-badge"
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={launch.mode === 'prelaunch'
                  ? { background: '#FEF3C7', color: '#92400E' }
                  : { background: '#DCFCE7', color: '#166534' }}
              >
                {launch.mode === 'prelaunch' ? '🔒 Pre-Launch (products hidden)' : '🟢 Live'}
              </span>
            </div>
          )}
        </div>
        <p className="text-sm mb-4" style={{ color: '#73766A' }}>
          Pre-Launch keeps the site open for signups while hiding all products behind a branded
          coming-soon page. Launching reveals everything and (optionally) emails your early signups.
        </p>

        {launch && (
          <>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#73766A' }}>Launch email subject</label>
                <Input
                  data-testid="launch-email-subject"
                  value={launchSubject}
                  onChange={(e) => setLaunchSubject(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#73766A' }}>
                  Launch email message ({'{name}'} becomes each person's first name)
                </label>
                <textarea
                  data-testid="launch-email-body"
                  value={launchBody}
                  onChange={(e) => setLaunchBody(e.target.value)}
                  rows="10"
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs" style={{ color: '#73766A' }}>
                  Will send to <b>{launch.recipient_count ?? 0}</b> signup(s) with emails enabled.
                  {launch.last_email_result && (
                    <> Last blast: {launch.last_email_result.sent} sent, {launch.last_email_result.failed} failed.</>
                  )}
                </p>
                <Button data-testid="save-launch-draft-btn" variant="outline" size="sm" onClick={saveLaunchTemplate} className="bg-white text-[#20212B] border border-gray-300 text-xs">
                  Save draft
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap pt-3 border-t">
              {launch.mode === 'launched' ? (
                <Button
                  data-testid="enter-prelaunch-btn"
                  onClick={() => setConfirmPrelaunch(true)}
                  disabled={launchBusy}
                  variant="outline"
                  className="border-amber-500 text-amber-700 hover:bg-amber-50"
                >
                  🔒 Enter Pre-Launch
                </Button>
              ) : (
                <>
                  <Button
                    data-testid="launch-now-btn"
                    onClick={() => setConfirmLaunch(true)}
                    disabled={launchBusy}
                    className="text-white"
                    style={{ background: 'linear-gradient(135deg, #9E6752 0%, #C97C5F 100%)' }}
                  >
                    <Rocket className="w-4 h-4 mr-2" /> Launch Mementa
                  </Button>
                  <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: '#20212B' }}>
                    <input
                      data-testid="launch-send-email-checkbox"
                      type="checkbox"
                      checked={sendEmailOnLaunch}
                      onChange={(e) => setSendEmailOnLaunch(e.target.checked)}
                      className="w-4 h-4"
                    />
                    Send the launch email
                  </label>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmPrelaunch}
        onOpenChange={setConfirmPrelaunch}
        onConfirm={enterPrelaunch}
        title="Enter Pre-Launch mode?"
        description="Visitors will see a branded coming-soon page instead of products. Signups stay open, and you (as admin) still see everything normally."
        confirmText="Lock it down"
      />
      <ConfirmDialog
        open={confirmLaunch}
        onOpenChange={setConfirmLaunch}
        onConfirm={doLaunch}
        title="Launch Mementa? 🚀"
        description={sendEmailOnLaunch
          ? `This reveals all products to everyone AND sends your launch email to ${launch?.recipient_count ?? 0} signup(s). Your current draft will be used.`
          : 'This reveals all products to everyone. No email will be sent.'}
        confirmText="Launch"
      />

      {/* Data cleanup */}
      <div className="bg-white rounded-xl p-5 mt-6 shadow-sm" data-testid="data-cleanup-card">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-1" style={{ color: '#20212B' }}>
          <Trash2 className="w-5 h-5" style={{ color: '#9E6752' }} /> Data Cleanup
        </h2>
        <p className="text-sm mb-4" style={{ color: '#73766A' }}>
          Housekeeping tools. Each action is permanent.
        </p>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-medium" style={{ color: '#20212B' }}>Expired shared AI links</p>
            <p className="text-xs" style={{ color: '#73766A' }}>Delete shared gift-shortlist links past their 7-day expiry.</p>
          </div>
          <Button
            data-testid="clear-expired-shortlists-btn"
            variant="outline"
            size="sm"
            disabled={cleaning}
            onClick={clearExpiredShortlists}
            className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> {cleaning ? 'Clearing…' : 'Delete expired'}
          </Button>
        </div>
      </div>
    </div>
  );
}
